import { CartService } from '@b2b/common/services';
import { BugsnagService } from '@b2b/common/services/bugsnag';
import { PendoService } from '@b2b/common/services/pendo';
import { extractACMEErrorType, FeatureFlag, TODO } from '@b2b/types/core';
import {
  Rejection,
  RejectType,
  StateDeclaration,
  StateProvider,
  StateService,
  Trace,
  Transition,
  TransitionService,
  UrlRouterProvider,
} from '@uirouter/angularjs';
import angular, {
  IAngularEvent,
  IHttpProvider,
  IHttpService,
  ILocationProvider,
  ILocationService,
  ILogService,
  IQService,
  IRootScopeService,
  IScope,
  ITimeoutService,
  IWindowService,
} from 'angular';
import acmeModule from './app.module';
import { ConflictInterceptorError } from './common/factories/conflictInterceptor/conflictInterceptor.factory';
import { ApiService } from './common/services/api/types';
import { AuthenticationService } from './common/services/auth/authentication.service';
import { FeatureService } from './common/services/feature/feature.service';
import { NavigationService } from './common/services/navigation/navigation.service';
import { PermissionsService } from './common/services/permissions';
import { ThemesHelperService } from './common/services/themesHelper';
import { Observable } from 'rxjs';

export default acmeModule
  .config([
    '$stateProvider',
    '$urlRouterProvider',
    '$locationProvider',
    '$animateProvider',
    '$httpProvider',
    'localStorageServiceProvider',
    '$translateProvider',
    '$provide',
    'growlProvider',
    'uibDatepickerConfig',
    function AppConfig(
      $stateProvider: StateProvider,
      $urlRouterProvider: UrlRouterProvider,
      $locationProvider: ILocationProvider,
      $animateProvider: TODO,
      $httpProvider: IHttpProvider,
      localStorageServiceProvider: TODO,
      $translateProvider: TODO,
      $provide: TODO,
      growlProvider: TODO,
      uibDatepickerConfig: TODO,
    ) {
      $locationProvider.html5Mode({
        enabled: true,
        requireBase: false,
      });
      // If 404 App will route to this URL
      $urlRouterProvider.otherwise('/login');
      // Chrome bug with ngGrid and ng-animate
      $animateProvider.classNameFilter(/modal/);
      // Set up local storage
      localStorageServiceProvider.setPrefix('RBAC');
      // Set up l10n strings
      //  $translateProvider
      //    .useStaticFilesLoader({
      //      files: [{
      //        prefix: '/assets/lang/',
      //        suffix: '.strings'
      //      }]
      //    })
      //    .preferredLanguage('en-US');

      $httpProvider.interceptors.push('AuthInterceptorFactory');
      $httpProvider.interceptors.push('PermissionsInterceptorFactory');
      $httpProvider.interceptors.push('ConflictInterceptorFactory');
      $httpProvider.defaults.xsrfHeaderName = 'X-CSRF-Token';
      $httpProvider.defaults.xsrfCookieName = 'XSRF-TOKEN';

      growlProvider.globalDisableCountDown(true);
      growlProvider.globalTimeToLive({ success: 5000 });

      // Accessibility decorations for Angular-bootstrap main templates
      $provide.decorator('uibModalWindowDirective', [
        '$delegate',
        function ($delegate) {
          $delegate[0].templateUrl = 'common/includes/accessibleModalWindow.tpl.html';
          return $delegate;
        },
      ]);

      $provide.decorator('uibDaypickerDirective', [
        '$delegate',
        function ($delegate) {
          $delegate[0].templateUrl = 'common/includes/datePickerDay.tpl.html';
          return $delegate;
        },
      ]);

      /**
       * Michael Kropat
       * http://www.unknownerror.org/opensource/kriskowal/q/q/stackoverflow/18888104/angularjs-q-wait-for-all-even-when-1-rejected
       */
      $provide.decorator('$q', [
        '$delegate',
        function ($delegate) {
          const $q = $delegate;

          $q.allSettled =
            $q.allSettled ||
            function (promises) {
              const toSettle: any[] = [];

              if (angular.isArray(promises)) {
                angular.forEach(promises, function (promise, key) {
                  const dfd = $q.defer();
                  promise.then(dfd.resolve, dfd.resolve);
                  toSettle.push(dfd.promise);
                });
              }

              return $q.all(toSettle);
            };

          return $q;
        },
      ]);

      uibDatepickerConfig.showWeeks = false;
    },
  ])
  .run([
    '$rootScope',
    '$state',
    '$log',
    '$http',
    '$transitions',
    '$trace',
    '$uibModalStack',
    'growlMessages',
    'AuthService',
    'AuthenticationService',
    'ApiService',
    'CartService',
    'ConfigService',
    'LoggerService',
    'FeatureService',
    'FeatureToggleService',
    'ThemesHelperService',
    'TenantService',

    function run(
      $rootScope: IRootScopeService,
      $state: StateService,
      $log: ILogService,
      $http: IHttpService,
      $transitions: TransitionService,
      $trace: Trace,
      $uibModalStack: TODO,
      growlMessages: TODO,
      AuthService: TODO,
      authenticationService: AuthenticationService,
      ApiService: ApiService,
      CartService: CartService,
      ConfigService: TODO,
      LoggerService: TODO,
      FeatureService: FeatureService,
      FeatureToggleService: TODO,
      ThemesHelperService: ThemesHelperService,
      TenantService: TODO,
    ) {
      // $trace.enable('TRANSITION');

      AuthService.updateCSRF();
      AuthService.killStorage();

      /**
       * Handle permissions when going between states.
       *
       */
      $transitions.onStart({}, function (transition: Transition) {
        const checksum = ConfigService.get('app', 'rbacChecksum');
        const hasSettings = ThemesHelperService.checkExists();
        const to = transition.to();
        const toParams = transition.params();
        const from = transition.from();
        const fromParams = transition.params('from');

        const checkPermissions = function (permissions: string | string[], featureFlags: FeatureFlag[]) {
          const user = authenticationService.getUserFromCookie();
          const hasRequiredFeatureFlags = featureFlags.every((flag) => ThemesHelperService.getFeatureFlag(flag));

          if (
            !hasRequiredFeatureFlags ||
            !AuthService.hasPermission(permissions) ||
            (user != null && !AuthService.testChecksum(ConfigService.get('app', 'rbacChecksum')))
          ) {
            permissionError({ data: { success: true } });
          }
        };

        const permissionError = (response: { data: { success: boolean } }) => {
          const success = response && response.data && response.data.success;

          transition.abort();
          $rootScope.$broadcast('Permissions::error', {
            type: 'permissions_invalid',
          });

          if (success) {
            $state.go(authenticationService.getRedirectState() || 'login.default', {}, { notify: !!success });
          } else {
            // Could not get permissions, so log the user out.
            $state.go('application.failed', {}, { notify: false });
          }
        };

        const dismissModalsAndContinue = (to: StateDeclaration, from: StateDeclaration) => {
          // Close all modals in stack when switching views
          $uibModalStack.dismissAll();
          growlMessages.destroyAllMessages();

          $rootScope.$broadcast('AppController::startLoad', {
            to: to,
            toParams: toParams,
            from: from,
            fromParams: fromParams,
            event: transition,
          });
        };

        if (authenticationService.getUserFromCookie() && !checksum) {
          ApiService.tenant.config.list().then((response) => {
            if (response.data.payload.list.length > 0) {
              TenantService.setModel(response.data.payload.list[0]);
            }

            const user = authenticationService.getUserFromCookie();

            if (user != null) {
              ApiService.user.permissions
                .list(user.id)
                .then((response) => {
                  const permissions = response.data.payload;

                  if (TenantService.isRecurringPaymentEnabled() && permissions.indexOf('SUBSCRIPTION_VIEW') >= 0) {
                    permissions.push('RECURRING_PAYMENT_ENABLED');
                  }

                  AuthService.setPermissions(permissions);
                  $rootScope.$broadcast('Permissions::refresh');
                  checkPermissions(to.data.permissions, to.data.featureFlags ?? []);
                })
                .catch((response) => permissionError(response));
            }
          });
        } else {
          checkPermissions(to.data && to.data.permissions, to.data.featureFlags ?? []);
        }

        // check if themes are loaded so we can check the feature flags
        if (hasSettings) {
          if (
            angular.isDefined(to.data) &&
            FeatureService.isEnabled(FeatureService.objectLock) &&
            FeatureService.objectLock.isEligible(to, toParams) &&
            !FeatureService.objectLock.hasLock(to.name, toParams.id)
          ) {
            transition.abort();

            // TODO: show loading message
            // see ConflictInterceptor
            FeatureService.objectLock.api
              .create({ type: to.data.features.objectLock.type, id: toParams.id, force: toParams.force })
              .then(function () {
                FeatureService.objectLock.add({
                  views: to.data.features.objectLock.views,
                  id: toParams.id,
                });

                $state.go(to, toParams);
              });
          } else {
            if (angular.isDefined(from.data) && FeatureService.objectLock.canRelease(from, fromParams, to, toParams)) {
              FeatureService.objectLock.remove({
                view: from.name,
                id: fromParams.id,
              });
              FeatureService.objectLock.api.remove({ type: 'EventTemplate', id: fromParams.id });
            }
            dismissModalsAndContinue(to, from);
          }
        } else {
          // feature flags are not available, so fetch them, then reload the page.
          if (to.name?.startsWith('application')) {
            transition.abort();

            CartService.init();
            ApiService.themes.detail(true).then(
              function (response) {
                ThemesHelperService.resetModel();
                ThemesHelperService.setModel(response.data.payload);

                $state.go(to, toParams);
              },
              function () {
                $rootScope.$broadcast('Permissions::error', { type: 'permissions' });
              },
            );
          }
        }
      });

      $transitions.onError({}, function (transition: Transition) {
        const to = transition.to();
        const toParams = to.params;
        const from = transition.from();
        const fromParams = from.params ?? {};
        const transitionError = transition.error?.() as Rejection | undefined;

        const log = {
          to: to,
          toParams: toParams,
          from: from,
          fromParams: fromParams,
          error: transitionError
            ? { type: transitionError.type || 'unknown', message: transitionError.message || 'unknown' }
            : undefined,
        };

        const errorDetailStatus = transitionError?.detail?.status;
        const errorDetailType = extractACMEErrorType(transitionError?.detail?.data);

        if (
          /^application.failed*/.test(to.name ?? '') &&
          transitionError?.type !== RejectType.IGNORED &&
          transitionError?.type !== RejectType.SUPERSEDED &&
          transitionError?.type !== RejectType.ABORTED
        ) {
          // Already tried to show the failed page once, but that didn't work
          // Log the user out
          $state.go('logout.default');
        } else if (
          /^application.*/.test(to.name ?? '') &&
          transitionError?.type !== RejectType.IGNORED &&
          transitionError?.type !== RejectType.SUPERSEDED &&
          transitionError?.type !== RejectType.ABORTED &&
          errorDetailStatus !== 403 &&
          errorDetailType !== 'PASSWORD_EXPIRED'
        ) {
          try {
            LoggerService.log(log);
            console.error('Error on transition:', log);
            $state.go('application.failed');
          } catch (err) {
            console.error('Error logging transition error:', err);
          }
        }
      });
    },
  ])
  .controller('AppController', [
    '$window',
    '$scope',
    '$state',
    '$log',
    '$location',
    '$timeout',
    '$controller',
    '$q',
    '$transitions',
    'NavigationService',
    'AuthService',
    'AuthenticationService',
    'ConfigService',
    'ApiService',
    'HelpersService',
    'PermissionsService',
    'FeatureService',
    'ThemesHelperService',
    'FeatureToggleService',
    'CartService',
    'BugsnagService',
    'PendoService',
    'PrintService',
    function AppController(
      $window: IWindowService,
      $scope: IScope & { app: TODO; pageTitle: string; pageClass: string; savePreferences: boolean },
      $state: StateService,
      $log: ILogService,
      $location: ILocationService,
      $timeout: ITimeoutService,
      $controller: TODO,
      $q: IQService,
      $transitions: TransitionService,
      navigationService: NavigationService,
      AuthService: TODO,
      authenticationService: AuthenticationService,
      ConfigService: TODO,
      ApiService: ApiService,
      HelpersService: TODO,
      PermissionsService: PermissionsService,
      FeatureService: FeatureService,
      ThemesHelperService: ThemesHelperService,
      FeatureToggleService: TODO,
      CartService: CartService,
      bugsnagService: BugsnagService,
      pendoService: PendoService,
      printService: { print$: Observable<void> },
    ) {
      const ctrl = {
        init: function () {
          bugsnagService.start();
          pendoService.init();

          ctrl.initNavigation();

          $scope.$on('$destroy', () => {
            pendoService.destroy();
          });
          $scope.$on('LogoutController::logout', ctrl.onLogout);
          $scope.$on('LoginController::success', ctrl.onLoginSuccess);
          $scope.$on('AppController::users::invite', ctrl.onUsersInvite);
          $scope.$on('AppController::splashAnimate', ctrl.onSplashAnimate);
          $scope.$on('AppController::startLoad', ctrl.onStartLoad);
          $scope.$on('AppController::updatePageClass', ctrl.onUpdatePageClass);
          $scope.$on('AppController::endLoad', ctrl.onEndLoad);
          $scope.$on('AppController::updateTitle', ctrl.onUpdateTitle);
          // Print requests now arrive via PrintService (the migrated
          // <ng-header-print> publishes to it) instead of the `Header::print`
          // $scope event. $evalAsync bridges the RxJS callback into a digest.
          const printSubscription = printService.print$.subscribe(() =>
            $scope.$evalAsync(() => $scope.$broadcast('AppController::print')),
          );
          $scope.$on('$destroy', () => printSubscription.unsubscribe());
          $scope.$on('Cart::Open', ctrl.onCartOpen);
          $scope.$on('Cart::Close', ctrl.onCartClose);
          $scope.$on('Cart::Refresh', ctrl.onCartRefresh);
          $scope.$on('Permissions::refresh', ctrl.onPermissionsRefresh);
          $scope.$on(
            'AuthInterceptorFactory::error',
            (e, data: { type: 'PASSWORD_EXPIRED'; email: string; error: { type: string; message: string } }) => {
              switch (data.type) {
                case 'PASSWORD_EXPIRED': {
                  $state.go('resetPassword.passwordExpired', {
                    email: data.email,
                    error: data.error,
                  });
                  return;
                }

                default: {
                  // noop
                  return;
                }
              }
            },
          );
          $scope.$on('PermissionsInterceptorFactory::error', ctrl.onPermissionsError);
          $scope.$on('ConflictInterceptorFactory::error', ctrl.onConflictError);
          $scope.$on('Permissions::error', ctrl.onPermissionsError);

          $transitions.onSuccess({}, function (transition: Transition) {
            const to = transition.to();
            const toParams = transition.params();
            const from = transition.from();
            const fromParams = transition.params('from');
            navigationService.update(to.name);
            pendoService.trackPageLoad({ routeName: to.name });

            if (angular.isDefined(to.data.pageTitle)) {
              $scope.pageTitle = 'Acme: ' + to.data.pageTitle;
              $scope.pageClass = to.data.pageClass;
              console.log('UUID:', $scope.pageTitle, to.name);
            }

            if (angular.isDefined(from.data && from.data.pageTitle)) {
              $scope.savePreferences = from.data.enablePreferences;
            }

            if ($scope.savePreferences) {
              let preferenceKey = from.name;

              if (fromParams._preferenceKey) {
                preferenceKey = [preferenceKey, fromParams._preferenceKey].join('.');
              }

              const user = authenticationService.getUserFromCookie();

              if (user != null) {
                ConfigService.save({
                  type: ConfigService.settingsTypes.view,
                  name: preferenceKey,
                  id: user.id,
                });
              }
            }
          });

          $scope.$on('$translateLoadingStart', function () {
            // noop
          });
          $scope.$on('$translateLoadingSuccess', function () {
            // noop
          });
          $scope.$on('$translateLoadingError', function () {
            // noop
          });
          $scope.$on('$translateLoadingEnd', function () {
            // noop
          });

          if (
            !ConfigService.set({
              type: 'directives',
              name: 'header',
              value: { showInvite: AuthService.hasPermission('USERS_INVITE') },
            })
          ) {
            console.log('ConfigService -> directives -> header(fail):', ConfigService);
          }
        },

        initNavigation: function () {
          const navigationConfig = {
            default: [
              {
                name: 'cart',
                title: 'Cart',
                icon: 'cart',
                sref: 'application.cart',
                parentState: 'application.cart',
                hidden: true,
                permissions: ['EVENT_VIEW'],
              },
              {
                name: 'events',
                title: 'Events',
                icon: 'events',
                sref: 'application.programs.list',
                parentState: 'application.programs',
                nested: [
                  'application.scheduler.detail',
                  'application.templates.edit.description',
                  'application.templates.edit.pricing',
                  'application.templates.edit.addons',
                  'application.templates.edit.resources',
                  'application.templates.edit.exceptions',
                  'application.templates.edit.schedules',
                  'application.templates.edit.schedules.create.times',
                  'application.templates.edit.schedules.create.pricing',
                  'application.templates.edit.schedules.create.resources',
                  'application.templates.edit.schedules.create.tickets',
                  'application.templates.edit.schedules.create.forms',
                  'application.templates.edit.schedules.edit.times',
                  'application.templates.edit.schedules.edit.pricing',
                  'application.templates.edit.schedules.edit.resources',
                  'application.templates.edit.schedules.edit.resources',
                  'application.templates.edit.schedules.edit.tickets',
                  'application.templates.edit.schedules.edit.forms',
                  'application.templates.edit.flexpay',
                  'application.templates.edit.flexpay.create.schedule',
                  'application.templates.edit.flexpay.edit.schedule',
                  'application.templates.create.description',
                  'application.templates.create.pricing',
                  'application.templates.create.addons',
                  'application.templates.create.resources',
                  'application.templates.create.exceptions',
                  'application.templates.create.schedules',
                  'application.templates.create.schedules.create.times',
                  'application.templates.create.schedules.create.pricing',
                  'application.templates.create.schedules.create.resources',
                  'application.templates.create.schedules.edit.times',
                  'application.templates.create.schedules.edit.pricing',
                  'application.templates.create.schedules.edit.resources',
                  'application.templates.create.flexpay',
                  'application.templates.create.flexpay.create.schedule',
                  'application.templates.create.flexpay.edit.schedule',
                  'application.templates.reorder',
                ],
                permissions: [
                  'EVENT_VIEW',
                  'EVENT_DELETE',
                  'ORDER_REBOOK',
                  'ORDER_CREATE',
                  'ORDER_UPDATE',
                  'ACCT_ORDERS_CREATE',
                  'ACCT_ORDERS_UPDATE',
                  'ORDER_VIEW',
                  'EVENT_CALENDAR_VIEW',
                  'EVENT_TEMPLATE_VIEW',
                  'EVENT_TEMPLATE_REORDER',
                  'COMBO_TEMPLATE_VIEW',
                ],

                items: [
                  {
                    name: 'events.list',
                    title: 'Events List',
                    sref: 'application.programs.list',
                    permissions: ['*'],
                  },
                  {
                    name: 'calendar',
                    title: 'Calendar',
                    sref: 'application.programs.calendar.event',
                    permissions: ['EVENT_CALENDAR_VIEW'],
                    roles: ['personnel', 'admin'],
                  },
                  {
                    name: 'templates',
                    title: 'Templates',
                    sref: 'application.programs.templatesList',
                    roles: ['admin'],
                    permissions: ['EVENT_TEMPLATE_VIEW'],
                  },
                  {
                    name: 'combos',
                    title: 'Combos',
                    sref: 'application.programs.combos.list',
                    roles: ['admin'],
                    permissions: ['COMBO_TEMPLATE_VIEW'],
                  },
                ],
              },
              {
                name: 'orders',
                title: 'Orders',
                icon: 'orders',
                sref: 'application.orders.list',
                parentState: 'application.orders',
                permissions: ['ORDER_VIEW', 'ACCT_ORDERS_VIEW', 'ORDER_REQUEST_VIEW', 'OFFLINE_TRANSACTIONS_VIEW'],
                nested: [
                  'application.orders.list',
                  'application.orders.requests.list',
                  'application.orders.offline.list',
                ],
                items: [
                  {
                    name: 'orders.list',
                    title: 'Orders',
                    sref: 'application.orders.list',
                    permissions: ['ORDER_VIEW', 'ACCT_ORDERS_VIEW'],
                  },
                  {
                    name: 'orders.requests.list',
                    title: 'Order Requests',
                    sref: 'application.orders.requests.list',
                    permissions: ['ORDER_REQUEST_VIEW'],
                  },
                  {
                    name: 'application.orders.offline',
                    title: 'Offline Transactions',
                    sref: 'application.orders.offline.list',
                    permissions: ['OFFLINE_TRANSACTIONS_VIEW'],
                  },
                ],
              },
              {
                name: 'donations',
                title: 'Donations',
                icon: 'pricing',
                sref: 'application.donations.amount.list',
                parentState: 'application.donations',
                roles: ['admin'],
                permissions: ['ADDONS_VIEW'],
                nested: ['application.donations.amount.list', 'application.donations.config.list'],
                items: [
                  {
                    name: 'donations.amount',
                    title: 'Amount',
                    sref: 'application.donations.amount.list',
                    permissions: ['ADDONS_VIEW'],
                  },
                  {
                    name: 'donations.config',
                    title: 'Config',
                    sref: 'application.donations.config.list',
                    permissions: ['DONATIONS_CONFIG_VIEW'],
                  },
                ],
              },
              {
                name: 'customers',
                title: 'Customers',
                icon: 'customers',
                sref: 'application.customers.individuals.list',
                parentState: 'application.customers',
                permissions: ['CUSTOMERS_VIEW'],
              },
              {
                name: 'resources',
                title: 'Resources',
                icon: 'resources',
                sref: "application.entities.list({ type: 'personnel' })",
                parentState: 'application.entities',
                permissions: [
                  'RESOURCES_REQUESTS_VIEW',
                  'ENTITY_PERSONNEL_VIEW',
                  'ENTITY_EQUIPMENT_VIEW',
                  'ENTITY_FACILITY_VIEW',
                ],
                nested: ['application.resources.create', 'application.resources.requests'],

                items: [
                  {
                    name: 'resources.requests',
                    title: 'Assignments',
                    sref: 'application.resources.requests',
                    permissions: ['RESOURCES_REQUESTS_VIEW'],
                  },
                  {
                    name: 'entities.personnel',
                    title: 'People',
                    sref: "application.entities.list({ type: 'personnel' })",
                    permissions: ['ENTITY_PERSONNEL_VIEW'],
                  },
                  {
                    name: 'entities.facilities',
                    title: 'Places',
                    sref: "application.entities.list({ type: 'facilities' })",
                    permissions: ['ENTITY_FACILITY_VIEW'],
                  },
                  {
                    name: 'entities.equipment',
                    title: 'Equipment',
                    sref: "application.entities.list({ type: 'equipment' })",
                    permissions: ['ENTITY_EQUIPMENT_VIEW'],
                  },
                ],
              },
              {
                name: 'forms',
                title: 'Forms',
                icon: 'forms',
                sref: 'application.forms.list',
                parentState: 'application.forms',
                permissions: ['FORMS_VIEW'],
              },
              {
                name: 'inventory',
                title: 'Add-ons',
                icon: 'addons',
                sref: 'application.inventory.list',
                parentState: 'application.inventory',
                roles: ['admin'],
                permissions: ['ADDONS_VIEW'],
              },
              {
                name: 'membership',
                title: 'Membership',
                icon: 'users',
                sref: 'application.membership.list',
                parentState: 'application.membership',
                roles: ['admin'],
                permissions: ['MEMBERSHIP_VIEW', 'MEMBERSHIP_CATEGORY_VIEW', 'MEMBERSHIP_SALES'],

                items: [
                  {
                    name: 'application.membership.list',
                    title: 'Members',
                    sref: 'application.membership.list',
                    permissions: ['MEMBERSHIP_VIEW'],
                  },
                  {
                    name: 'application.membership.levels',
                    title: 'Levels',
                    sref: 'application.membership.levels',
                    permissions: ['MEMBERSHIP_CATEGORY_VIEW'],
                  },
                  {
                    name: 'application.membership.rules.lifecycle',
                    title: 'Rules',
                    sref: 'application.membership.rules.lifecycle',
                    permissions: ['MEMBERSHIP_CATEGORY_VIEW'],
                  },
                  {
                    name: 'application.membership.mapping',
                    title: 'External CRM Mapping',
                    sref: 'application.membership.mapping',
                    permissions: ['MEMBERSHIP_CATEGORY_VIEW'],
                  },
                  {
                    name: 'application.membership.retail',
                    title: 'Purchase',
                    sref: 'application.membership.retail',
                    permissions: FeatureToggleService.check('TENANT_CART') ? ['MEMBERSHIP_SALES'] : [],
                  },
                ],
              },
              {
                name: 'subscriptions',
                title: 'Subscriptions',
                icon: 'autorenew',
                sref: 'application.subscriptions.list',
                parentState: 'application.subscriptions',
                permissions: ['RECURRING_PAYMENT_ENABLED'],
              },
              {
                name: 'pricing',
                title: 'Pricing',
                icon: 'pricing',
                sref: 'application.pricing.lists',
                parentState: 'application.pricing',
                roles: ['admin'],
                permissions: [
                  'PRICE_LISTS_VIEW',
                  'PERSON_TYPES_VIEW',
                  'DISCOUNTS_VIEW',
                  'COUPONS_VIEW',
                  'COUPONS_CREATE',
                  'COUPONS_DELETE',
                  'COUPONS_UPDATE',
                  'COUPON_CATEGORIES_VIEW',
                  'COUPON_CATEGORIES_CREATE',
                  'COUPON_CATEGORIES_DELETE',
                  'COUPON_CATEGORIES_UPDATE',
                ],
                nested: [
                  'application.coupons.list',
                  'application.coupons.categories',
                  'application.coupons.categories.create',
                  'application.coupons.create',
                  'application.coupons.create.detail',
                  'application.coupons.create.schedule',
                ],

                items: [
                  {
                    name: 'application.pricing.lists',
                    title: 'Price Lists',
                    sref: 'application.pricing.lists',
                    permissions: ['PRICE_LISTS_VIEW'],
                  },
                  {
                    name: 'application.pricing.categories',
                    title: 'Categories',
                    sref: 'application.pricing.categories',
                    permissions: ['PERSON_TYPES_VIEW'],
                  },
                  {
                    name: 'application.pricing.discounts',
                    title: 'Discounts',
                    sref: 'application.pricing.discounts',
                    permissions: ['DISCOUNTS_VIEW'],
                  },
                  {
                    name: 'coupons',
                    title: 'Coupons',
                    sref: 'application.coupons.list',
                    permissions: ['COUPONS_VIEW'],
                  },
                ],
              },
              {
                name: 'partners',
                title: 'Organizations',
                icon: 'partners',
                sref: 'application.partners.list.default',
                parentState: 'application.partners',
                roles: ['admin'],
                permissions: ['ACCT_VIEW', 'ACCT_ORDERS_VIEW', 'ACCT_CATEGORIES_VIEW'],

                items: [
                  {
                    name: 'application.partners.list',
                    title: 'Organizations List',
                    sref: 'application.partners.list.default',
                    permissions: ['ACCT_VIEW'],
                  },
                  {
                    name: 'partners.list.categories',
                    title: 'Categories',
                    sref: 'application.partners.list.categories',
                    permissions: ['ACCT_CATEGORIES_VIEW'],
                  },
                ],
              },
              {
                name: 'reports',
                title: 'Reports',
                sref: 'application.reports.list',
                icon: 'reporting',
                parentState: 'application.reports',
                roles: ['admin'],
                permissions: ['REPORTS_VIEW'],
              },
              {
                name: 'finance',
                title: 'Finance',
                sref: 'application.finance.list',
                icon: 'finance',
                parentState: 'application.finance',
                permissions: ['FINANCE_REPORTS_VIEW'],
              },
              {
                name: 'users',
                title: 'Users',
                icon: 'users',
                sref: 'application.users.list',
                parentState: 'application.users',
                roles: ['admin'],
                nested: [
                  'application.groups.list',
                  'application.groups.users',
                  'application.groups.roles',
                  'application.roles.list',
                  'application.roles.permissions',
                  'application.permissions',
                  'application.permissions.list',
                ],
                permissions: ['USERS_VIEW', 'USER_GROUPS_VIEW', 'PERMISSIONS_VIEW', 'ROLES_VIEW'],

                items: [
                  {
                    name: 'application.users',
                    title: 'Users List',
                    sref: 'application.users.list',
                    permissions: ['USERS_VIEW'],
                  },
                  {
                    name: 'application.groups',
                    title: 'Groups',
                    sref: 'application.groups.list',
                    permissions: ['USER_GROUPS_VIEW'],
                  },
                  {
                    name: 'application.roles',
                    title: 'Roles',
                    sref: 'application.roles.list',
                    permissions: ['ROLES_VIEW'],
                  },
                  {
                    name: 'application.permissions',
                    title: 'Permissions',
                    sref: 'application.permissions.list',
                    permissions: ['PERMISSIONS_VIEW'],
                  },
                ],
              },
              {
                name: 'themes',
                title: 'Theme',
                sref: 'application.themes',
                icon: 'themes',
                parentState: 'application.themes',
                roles: ['superuser'],
                permissions: ['WL_THEME_CONFIG_VIEW', 'WL_TICKET_CONFIG_VIEW', 'WL_POS_CONFIG_VIEW'],
                items: [
                  {
                    name: 'application.themes',
                    title: 'Theme',
                    sref: 'application.themes.web',
                    permissions: ['WL_THEME_CONFIG_VIEW', 'WL_TICKET_CONFIG_VIEW', 'WL_POS_CONFIG_VIEW'],
                    hidden: true,
                  },
                ],
              },
              {
                name: 'app-store',
                title: 'Apps',
                sref: 'application.app-store',
                icon: 'apps',
                parentState: 'application.app-store',
                permissions: ['APP_ADMIN'],
                hidden: !ThemesHelperService.getFeatureFlag('enableAppsIntegration'),
              },
              {
                name: 'settings',
                title: 'Settings',
                icon: 'settings',
                sref: 'application.account.profile',
                parentState: 'application.account',
                nested: [
                  'application.settings.fields',
                  'application.settings.terminals',
                  'application.account.administration',
                  'application.account.configuration',
                  'application.pos.list',
                  'application.pos.create.description',
                  'application.pos.create.pages',
                  'application.pos.create.images',
                  'application.pos.create.navigation',
                  'application.pos.create.settings',
                  'application.pos.edit.description',
                  'application.pos.edit.pages',
                  'application.pos.edit.images',
                  'application.pos.edit.navigation',
                  'application.pos.edit.settings',
                  'application.layouts.tickets.list',
                  'application.layouts.tickets.edit',
                  'application.layouts.tickets.edit.definition',
                  'application.layouts.tickets.edit.description',
                  'application.layouts.tickets.create',
                  'application.layouts.tickets.create.definition',
                  'application.layouts.tickets.create.description',
                  'application.layouts.membership.cards.list',
                  'application.passes.list',
                  'application.passes.edit',
                  'application.passes.edit.series',
                  'application.passes.edit.settings',
                  'application.passes.create',
                  'application.passes.create.series',
                  'application.passes.create.settings',
                  'application.passes.detail',
                  'application.passes.detail.series',
                  'application.passes.detail.settings',
                  'application.importExport.list',
                  'application.settings.integration',
                  'application.settings.integration.sync',
                  'application.settings.integration.logs',
                ],
                permissions: [
                  'PROFILE_UPDATE',
                  'COMPANY_PROFILE_VIEW',
                  'SETTLEMENT_VIEW',
                  'DEPT_VIEW',
                  'POS_SALE_TERMINALS_VIEW',
                  'POS_CONFIG_VIEW',
                  'CONF_VIEW',
                  'TICKET_LAYOUTS_VIEW',
                  'CUSTOM_FIELDS_VIEW',
                  'TENANT_ADMINISTRATION_VIEW',
                ],

                items: [
                  {
                    name: 'profile',
                    title: 'My Profile',
                    sref: 'application.account.profile',
                    permissions: ['*'],
                  },
                  {
                    name: 'company',
                    title: 'Company Profile',
                    sref: 'application.account.company',
                    roles: ['admin'],
                    permissions: ['COMPANY_PROFILE_VIEW'],
                  },
                  {
                    name: 'departments',
                    title: 'Departments',
                    sref: 'application.account.departments',
                    roles: ['admin'],
                    permissions: ['DEPT_VIEW'],
                  },
                  {
                    name: 'settings.terminals',
                    title: 'Sale Terminals',
                    sref: 'application.settings.terminals',
                    permissions: ['POS_SALE_TERMINALS_VIEW'],
                    roles: ['admin'],
                  },
                  {
                    name: 'application.pos.list',
                    title: 'POS Config',
                    sref: 'application.pos.list',
                    permissions: ['POS_CONFIG_VIEW'],
                  },
                  {
                    name: 'application.layouts.tickets.list',
                    title: 'Ticket Layouts',
                    sref: 'application.layouts.tickets.list',
                    permissions: ['TICKET_LAYOUTS_VIEW'],
                  },
                  {
                    name: 'application.passes.list',
                    title: 'Pass Ranges',
                    sref: 'application.passes.list',
                    permissions: ['PASS_RANGES_VIEW'],
                  },
                  {
                    name: 'layouts.membership.cards',
                    title: 'Card Layouts',
                    sref: 'application.layouts.membership.cards.list',
                    permissions: ['MEMBERSHIP_CARD_LAYOUTS_VIEW'],
                  },
                  {
                    name: 'settings.fields',
                    title: 'Custom Fields',
                    sref: 'application.settings.fields',
                    permissions: ['CUSTOM_FIELDS_VIEW'],
                  },
                  {
                    name: 'administration',
                    title: 'Administration',
                    sref: 'application.account.administration',
                    permissions: ['TENANT_ADMINISTRATION_VIEW'],
                  },
                  {
                    name: 'configuration',
                    title: 'Configuration',
                    sref: 'application.account.configuration',
                    permissions: ['CONF_VIEW'],
                  },
                  {
                    name: 'importExport.list',
                    title: 'Import Export',
                    sref: 'application.importExport.list',
                    permissions: ['IMPEX_EXECUTE'],
                  },
                  {
                    name: 'integration.sync',
                    title: 'Salesforce Managed',
                    sref: 'application.settings.integration.sync',
                    selected: ['application.settings.integration.sync', 'application.settings.integration.logs'],
                    permissions: ['SALESFORCE_INTEGRATION_UPDATE'],
                  },
                ],
              },
              {
                name: 'sherpa',
                title: 'CSR',
                icon: 'settings',
                sref: 'application.sherpa.customers.list',
                parentState: 'application.sherpa',
                roles: ['csm', 'csr'],
                permissions: ['CSR', 'CSM'],

                items: [
                  {
                    name: 'sherpa.customers',
                    title: 'Customers',
                    sref: 'application.sherpa.customers.list',
                    roles: ['csm', 'csr'],
                    permissions: ['CSR'],
                  },
                  {
                    name: 'sherpa.activities',
                    title: 'Activities',
                    sref: 'application.sherpa.activities.tenant',
                    roles: ['csm'],
                    permissions: ['CSM'],
                  },
                ],
              },
            ],
          };

          navigationService.init(navigationConfig, true, 'permissions');
        },

        onUsersInvite: function () {
          $scope.$broadcast('header::invite');
        },

        onSplashAnimate: function () {
          $('.application-splash').addClass('animate');
        },

        onUpdatePageClass: function (e: IAngularEvent, data: TODO) {
          $scope.pageClass = data.name;
        },

        onStartLoad: function (event, data) {
          console.log('AppController::onStartLoad -> show');
          $scope.$broadcast('contentHeader::startLoad');
          $scope.$broadcast('pageContent::startLoad', data);
        },

        onEndLoad: function () {
          console.log('AppController::onEndLoad -> hide');
          $scope.$broadcast('contentHeader::endLoad');
        },

        onLogout: function () {
          navigationService.reset();
          ThemesHelperService.resetModel();
          CartService.resetCart(true);
          FeatureToggleService.reset();
          $scope.$broadcast('AppController::logout::success');
        },

        onLoginSuccess: function () {
          ctrl.initNavigation();

          CartService.init();

          console.log('AppController::login::success -> isAdmin:', AuthService.hasRole('admin'));
          if (
            !ConfigService.set({
              type: 'directives',
              name: 'header',
              value: { showInvite: AuthService.hasPermission('USERS_INVITE') },
            })
          ) {
            console.log('ConfigService -> directives -> header(fail):', ConfigService);
          }
        },

        onUpdateTitle: function (e: IAngularEvent, data: TODO) {
          $scope.pageTitle = 'Acme: ' + data.title;
        },

        onCartOpen: function (e: IAngularEvent, data: TODO) {
          $scope.$broadcast('AppController::cart::open', data);
        },

        onCartClose: function (e: IAngularEvent, data: TODO) {
          $scope.$broadcast('AppController::cart::close', data);
        },

        onCartRefresh: function (e: IAngularEvent, data: TODO) {
          $scope.$broadcast('AppController::cart::refresh', data);
        },

        onPermissionsError: function (e: IAngularEvent, data: { type: 'permission' | 'session' }) {
          e.preventDefault();

          if (data.type === 'session') {
            if ($state.current.name && /^application/.test($state.current.name)) {
              $state.go('logout.default');
            }
          } else {
            // Instantiate controller with new $scope
            const PermissionsInterceptorViewModel = $scope.$new();
            const PermissionsInterceptorController = $controller('PermissionsInterceptorController', {
              $scope: PermissionsInterceptorViewModel,
              errorType: data.type,
            });

            // Broadcast error event to launch modal
            $scope.$broadcast('AppController::permissions::error', { errorType: data.type });
            // Destroy the scope and thereby the controller as well
            PermissionsInterceptorViewModel.$destroy();
          }
        },

        onConflictError: function (e: IAngularEvent, data: ConflictInterceptorError) {
          // Instantiate controller with new $scope
          const hasOverridePermission = PermissionsService.check('UNLOCK_LOCKED');
          let errorType = data.type;
          const errorData = data.errorData;
          const ConflictInterceptorViewModel = $scope.$new();

          if (errorType && errorType === 'object_lock' && hasOverridePermission) {
            errorType = 'object_lock_override';
          }

          const ConflictInterceptorController = $controller('ConflictInterceptorController', {
            $scope: ConflictInterceptorViewModel,
            errorType: errorType,
            errorData: errorData,
          });

          // Broadcast error event to launch modal
          $scope.$broadcast('AppController::conflict::error', { errorType: errorType, errorData: errorData });
          // Destroy the scope and thereby the controller as well
          ConflictInterceptorViewModel.$destroy();
        },

        onPermissionsRefresh: function (e: IAngularEvent, data: TODO) {
          ctrl.initNavigation();
          navigationService.update();
        },
      };

      $scope.app = ctrl;
      ctrl.init();
    },
  ]).name;
